/* ─────────── Shared receipt renderer ───────────
   แยกออกจาก ReceiptEditor.jsx เพื่อให้หน้า ReceiptArchive (คลังใบเสร็จ)
   render/พิมพ์/ดาวน์โหลด PDF ใบเสร็จได้หน้าตาเหมือนตอนออกทุกประการ */

export const fmt = n => Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 });
export const fmtDate = d =>
  d ? new Date(d).toLocaleDateString("th-TH", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "";

/* วันที่แบบไทยย่อคั่นทับ พ.ศ. เต็ม เช่น "16/ส.ค./2569" — ใช้กับข้อความที่พิมพ์ลงเอกสาร */
export const fmtDateThai = d => {
  if (!d) return "";
  const dt = new Date(d);
  return [
    String(dt.getDate()).padStart(2, "0"),
    dt.toLocaleDateString("th-TH", { month: "short" }),
    dt.getFullYear() + 543,
  ].join("/");
};

export function extractLatinName(name = "") {
  return name.replace(/[฀-๿]/g, "").trim();
}

/* รายละเอียดใต้ชื่อรายการ — เก็บเป็นสตริงเดียวคั่นด้วย \n (ใบเก่าที่มีบรรทัดเดียวใช้ได้เหมือนเดิม)
   บรรทัดว่างไม่พิมพ์ลงเอกสาร */
export const subLines = item => String(item?.sub || "").split("\n").filter(l => l.trim());

/* แปลงจำนวนเงินเป็นตัวอักษรภาษาไทย */
export function bahtText(num) {
  num = Number(num || 0);
  const t = ["", "หนึ่ง", "สอง", "สาม", "สี่", "ห้า", "หก", "เจ็ด", "แปด", "เก้า"];
  const p = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน"];
  const read = n => {
    let s = "";
    const str = String(n);
    const len = str.length;
    for (let i = 0; i < len; i++) {
      const d = Number(str[i]);
      const pos = len - i - 1;
      if (d === 0) continue;
      if (pos === 0 && d === 1 && len > 1) s += "เอ็ด";
      else if (pos === 1 && d === 2) s += "ยี่สิบ";
      else if (pos === 1 && d === 1) s += "สิบ";
      else s += t[d] + p[pos];
    }
    return s;
  };
  const readInt = n => {
    if (n === 0) return "ศูนย์";
    let s = "";
    const millions = Math.floor(n / 1000000);
    const rest = n % 1000000;
    if (millions) s += readInt(millions) + "ล้าน";
    if (rest) s += read(rest);
    return s;
  };
  const [intPart, decPart] = num.toFixed(2).split(".");
  let text = readInt(Number(intPart)) + "บาท";
  const sat = Number(decPart);
  text += sat === 0 ? "ถ้วน" : read(sat) + "สตางค์";
  return text;
}

/* ─────────── ReceiptDocument ─────────── */
/* variant: "original" = สำหรับลูกค้า (โทนเทา) | "copy" = สำเนา สำหรับบริษัท (โทนส้ม) */
export function ReceiptDocument({ data, innerRef, variant = "original" }) {
  const {
    hotelName, hotelNameEn, hotelAddress, hotelTaxId, logo, signature,
    receiptNo, receiptDate,
    customerName, customerAddress, customerTaxId,
    customerBranchType, customerBranchNo,
    items, note, includeVat, bankInfo, bankAccountName,
  } = data;

  const isHeadOffice = customerBranchType !== "branch";

  /* หน้าสำเนาหน้าตาเหมือนต้นฉบับทุกอย่าง ต่างกันแค่ข้อความในกล่องหัวเรื่อง */
  const isCopy = variant === "copy";
  const ACCENT  = "#6b6b6b";   // หัวตาราง
  const VALUE   = "#1a1a1a";   // สีตัวอักษรข้อมูล
  const BORDER  = "1.5px solid #1a1a1a";
  const CELL_V  = "1px solid #1a1a1a";

  const subtotal = items.reduce((s, i) => s + Number(i.qty || 1) * Number(i.price || 0), 0);
  const vatAmt   = includeVat ? subtotal * 0.07 : 0;
  const gross    = subtotal + vatAmt;
  const whtAmt   = includeVat ? subtotal * 0.03 : 0;
  const grand    = gross - whtAmt;

  /* วันที่บนเอกสาร — ไทยเต็ม พ.ศ. เช่น "18 มิถุนายน 2569" (ระบุ ca-buddhist ไว้กัน ICU บางตัวคืน ค.ศ.) */
  const fmtShort = d => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("th-TH-u-ca-buddhist",
      { day: "numeric", month: "long", year: "numeric" });
  };

  const displayDate = fmtShort(receiptDate);
  const latinName   = hotelNameEn || extractLatinName(hotelName);

  const underline = { borderBottom: "1px dotted #9ca3af", color: VALUE, paddingBottom: 1 };

  /* แถวว่างเติมตารางให้สูงเท่าในแบบฟอร์ม (ชื่อรายการ 1 บรรทัด + รายละเอียดอีกกี่บรรทัดก็ตาม) */
  const usedLines = items.reduce((s, i) => s + 1 + subLines(i).length, 0);
  const padLines  = Math.max(0, 20 - usedLines);

  /* ความกว้างคอลัมน์ขวาของตาราง — กล่องยอดรวมด้านล่างใช้ค่าเดียวกัน เส้นตั้งจะได้ตรงกัน */
  const COL_QTY = 86, COL_UNIT = 100, COL_AMT = 110;
  const TOTALS_W = COL_QTY + COL_UNIT + COL_AMT;

  const thStyle = (extra = {}) => ({
    background: ACCENT, color: "#fff", fontWeight: 600, fontSize: 13,
    padding: "7px 8px", border: CELL_V, ...extra,
  });
  const tdSide = (extra = {}) => ({
    borderLeft: CELL_V, borderRight: CELL_V, padding: "4px 8px",
    fontSize: 13, verticalAlign: "top", ...extra,
  });

  return (
    <div ref={innerRef} className="receipt-page" style={{
      width: 794, minHeight: 1123, background: "#fff",
      fontFamily: "'Noto Sans Thai',sans-serif",
      fontSize: 14, color: "#1a1a1a", padding: "32px 36px",
      boxSizing: "border-box", lineHeight: 1.5, position: "relative",
      display: "flex", flexDirection: "column",
    }}>
      {/* HEADER : โลโก้ + ชื่อโรงแรม */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 4 }}>
        <div style={{ width: 110, height: 110, borderRadius: "50%", overflow: "hidden",
                      background: "#f3f4f6", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
          {logo
            ? <img src={logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <span style={{ fontSize: 18, color: "#9ca3af", fontWeight: 700 }}>LOGO</span>}
        </div>
        <div style={{ paddingTop: 4 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#7cb342", lineHeight: 1.15 }}>{hotelName || "โรงแรม"}</div>
          {latinName && <div style={{ fontSize: 22, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.15 }}>{latinName}</div>}
          {hotelAddress && <div style={{ fontSize: 12, color: "#374151", marginTop: 3 }}>{hotelAddress}</div>}
          {hotelTaxId && (
            <div style={{ fontSize: 11, color: "#374151", marginTop: 1 }}>
              เลขประจำตัวผู้เสียภาษี {hotelTaxId.replace(/\s/g, "")}
            </div>
          )}
        </div>
      </div>

      {/* TITLE BOX */}
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <div style={{ display: "inline-block", border: "1.5px solid #1a1a1a",
                      borderRadius: 10, padding: "8px 32px", boxShadow: "2px 3px 5px rgba(0,0,0,0.25)" }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{isCopy ? "สำเนาใบเสร็จ" : "ใบเสร็จรับเงิน"}</div>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
            {isCopy ? "Duplicated Receipt" : "Receipt"}
          </div>
        </div>
      </div>

      {/* ข้อมูลลูกค้า + เลขที่/วันที่ */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 24, marginBottom: 4, fontSize: 13 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 3 }}>
            <span style={{ whiteSpace: "nowrap", width: 62 }}>นามลูกค้า</span>
            <span style={{ ...underline, flex: 1 }}>{customerName || " "}</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <span style={{ whiteSpace: "nowrap", width: 62 }}>ที่อยู่</span>
            <span style={{ ...underline, flex: 1, whiteSpace: "pre-line" }}>{customerAddress || " "}</span>
          </div>
        </div>
        <div style={{ width: 220, flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 3 }}>
            <span style={{ whiteSpace: "nowrap", width: 36 }}>เลขที่</span>
            <span style={{ ...underline, flex: 1, fontWeight: 600 }}>{receiptNo || " "}</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <span style={{ whiteSpace: "nowrap", width: 36 }}>วันที่</span>
            <span style={{ ...underline, flex: 1 }}>{displayDate || " "}</span>
          </div>
        </div>
      </div>

      {/* เลขประจำตัวผู้เสียภาษี + สำนักงานใหญ่/สาขา */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 13, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ whiteSpace: "nowrap" }}>เลขประจำตัวผู้เสียภาษี</span>
        <span style={{ ...underline, minWidth: 160, display: "inline-block", textAlign: "center" }}>
          {customerTaxId || " "}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span style={{ display: "inline-flex", width: 15, height: 15, border: "1.5px solid #1a1a1a",
                         alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
            {isHeadOffice ? "X" : ""}
          </span>
          <span>สำนักงานใหญ่</span>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span style={{ display: "inline-flex", width: 15, height: 15, border: "1.5px solid #1a1a1a",
                         alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
            {isHeadOffice ? "" : "X"}
          </span>
          <span>สาขาที่</span>
          <span style={{ ...underline, minWidth: 56, display: "inline-block", textAlign: "center" }}>
            {isHeadOffice ? " " : (customerBranchNo || " ")}
          </span>
        </span>
      </div>

      {/* ITEMS TABLE */}
      {/* tableLayout: fixed — บังคับให้คอลัมน์กว้างตามที่กำหนดเป๊ะ ไม่ให้เบราว์เซอร์ปรับตามเนื้อหา
          ไม่งั้นเส้นตั้งจะไม่ตรงกับกล่องยอดรวมด้านล่าง */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: BORDER, tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th style={thStyle({ width: 46, textAlign: "center" })}>ลำดับ</th>
            <th style={thStyle({ textAlign: "center" })}>รายการ</th>
            <th style={thStyle({ width: COL_QTY,  textAlign: "center" })}>จำนวนคืนที่พัก</th>
            <th style={thStyle({ width: COL_UNIT, textAlign: "center" })}>ราคา/หน่วย</th>
            <th style={thStyle({ width: COL_AMT,  textAlign: "center" })}>จำนวนเงิน</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const lineTotal = Number(item.qty || 1) * Number(item.price || 0);
            return (
              <tr key={i}>
                <td style={tdSide({ textAlign: "center" })}>{i + 1}</td>
                <td style={tdSide()}>
                  <div>{item.name || ""}</div>
                  {subLines(item).map((line, li) => <div key={li}>{line}</div>)}
                </td>
                <td style={tdSide({ textAlign: "center" })}>{item.qty || 1}</td>
                <td style={tdSide({ textAlign: "right" })}>{fmt(item.price)}</td>
                <td style={tdSide({ textAlign: "right" })}>{fmt(lineTotal)}</td>
              </tr>
            );
          })}
          {/* แถวว่างเติมความสูงตามฟอร์ม */}
          {Array.from({ length: padLines }).map((_, i) => (
            <tr key={`pad-${i}`}>
              <td style={tdSide()}>&nbsp;</td>
              <td style={tdSide()} />
              <td style={tdSide()} />
              <td style={tdSide()} />
              <td style={tdSide()} />
            </tr>
          ))}
        </tbody>
      </table>

      {/* FOOTER : หมายเหตุ + ยอดรวม */}
      <div style={{ display: "flex", border: BORDER, borderTop: "none" }}>
        {/* ซ้าย: หมายเหตุ + บัญชีธนาคาร */}
        <div style={{ flex: 1, padding: "8px 10px", fontSize: 11.5, color: "#374151" }}>
          <div>หมายเหตุ : {note || ""}</div>
          {bankInfo && <div style={{ marginTop: 4 }}>{bankInfo}</div>}
          {bankAccountName && <div>{bankAccountName}</div>}
        </div>
        {/* ขวา: ตารางยอดรวม */}
        {/* flex column + แถวสุดท้าย flex:1 — ให้กล่องยอดรวมยืดเต็มความสูงเท่าฝั่งหมายเหตุ
            ไม่งั้นเส้นตั้งคั่นคอลัมน์จำนวนเงินจะลากลงมาไม่สุดขอบล่าง */}
        <div style={{ width: TOTALS_W, flexShrink: 0, borderLeft: CELL_V,
                      display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", borderBottom: CELL_V }}>
            <div style={{ flex: 1, padding: "6px 12px", fontSize: 13 }}>รวมเงิน</div>
            <div style={{ width: COL_AMT, padding: "6px 12px", fontSize: 13, textAlign: "right", borderLeft: CELL_V }}>{fmt(subtotal)}</div>
          </div>
          {includeVat && (<>
            <div style={{ display: "flex", borderBottom: CELL_V }}>
              <div style={{ flex: 1, padding: "6px 12px", fontSize: 13 }}>ภาษีมูลค่าเพิ่ม<span style={{ marginLeft: 24 }}>7%</span></div>
              <div style={{ width: COL_AMT, padding: "6px 12px", fontSize: 13, textAlign: "right", borderLeft: CELL_V }}>{fmt(vatAmt)}</div>
            </div>
            <div style={{ display: "flex", borderBottom: CELL_V }}>
              <div style={{ flex: 1, padding: "6px 12px", fontSize: 13 }}>รวมเงินทั้งสิ้น</div>
              <div style={{ width: COL_AMT, padding: "6px 12px", fontSize: 13, textAlign: "right", borderLeft: CELL_V }}>{fmt(gross)}</div>
            </div>
            <div style={{ display: "flex", borderBottom: CELL_V }}>
              <div style={{ flex: 1, padding: "6px 12px", fontSize: 13 }}>หัก ณ ที่จ่าย<span style={{ marginLeft: 24 }}>3%</span></div>
              <div style={{ width: COL_AMT, padding: "6px 12px", fontSize: 13, textAlign: "right", borderLeft: CELL_V }}>{fmt(whtAmt)}</div>
            </div>
          </>)}
          <div style={{ display: "flex", flex: 1 }}>
            <div style={{ flex: 1, padding: "6px 12px", fontSize: 13, fontWeight: 600 }}>จำนวนเงินทั้งสิ้น</div>
            <div style={{ width: COL_AMT, padding: "6px 12px", fontSize: 13, fontWeight: 700, textAlign: "right", borderLeft: CELL_V }}>{fmt(grand)}</div>
          </div>
        </div>
      </div>

      {/* ตัวอักษร */}
      <div style={{ display: "flex", marginTop: 6 }}>
        <div style={{ flex: 1, background: "#e5e5e5", border: "1px solid #9ca3af",
                      display: "flex", alignItems: "center", padding: "7px 12px", fontSize: 13 }}>
          <span style={{ fontWeight: 600, marginRight: 16, whiteSpace: "nowrap" }}>จำนวนเงินทั้งสิ้น (ตัวอักษร)</span>
          <span style={{ flex: 1, textAlign: "center", fontWeight: 600 }}>({bahtText(grand)})</span>
        </div>
        {/* +1 = เส้นขอบขวาของกล่อง FOOTER — ให้ขอบขวาแถบเทาตรงกับเส้นตั้ง
           ที่คั่นหมายเหตุกับตารางยอดรวมพอดี */}
        <div style={{ width: TOTALS_W + 1, flexShrink: 0 }} />
      </div>

      {/* SIGNATURE BOXES */}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        {/* ฝั่งซ้ายเว้นว่าง — กล่องลงนามคงเหลือเฉพาะฝั่งโรงแรม */}
        <div style={{ flex: 2 }} />
        <div style={{ flex: 1, border: BORDER, padding: "8px 14px 12px", fontSize: 12, textAlign: "center" }}>
          <div style={{ marginBottom: 4 }}>ผู้ออกเอกสาร - {hotelName || "โรงแรม"}</div>
          <div style={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {signature && <img src={signature} alt="signature" style={{ maxHeight: 40, maxWidth: 120, objectFit: "contain" }} />}
          </div>
          <div style={{ borderBottom: "1px dotted #9ca3af", marginBottom: 4 }} />
          <div style={{ fontWeight: 600 }}>(ชื่อผู้รับเงิน)</div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── ScaledPreview ─────────── */
export function ScaledPreview({ data }) {
  const PREVIEW_WIDTH = 370, DOC_WIDTH = 794;
  const scale = PREVIEW_WIDTH / DOC_WIDTH;
  return (
    <div style={{ width:PREVIEW_WIDTH, height:Math.round(1123 * scale),
                  overflow:"hidden", position:"relative", borderRadius:8,
                  border:"1px solid #e5e7eb", boxShadow:"0 2px 12px rgba(0,0,0,0.08)", background:"#fff" }}>
      <div style={{ position:"absolute", top:0, left:0, transformOrigin:"top left",
                    transform:`scale(${scale})`, width:DOC_WIDTH, pointerEvents:"none" }}>
        <ReceiptDocument data={data} innerRef={null} />
      </div>
    </div>
  );
}

/* ─────────── openPrintWindow ───────────
   เปิดหน้าต่างพิมพ์ด้วย HTML + CSS ชุดเดียวกันเสมอ (editor และ archive) */
export function openPrintWindow(content, title = "ใบเสร็จ") {
  if (!content) return;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Noto Sans Thai',sans-serif;background:#fff}
@media print{@page{size:A4;margin:15mm 18mm}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}body>div{padding:0!important}}</style>
</head><body>${content}</body></html>`);
  w.document.close();
  w.onload = () => setTimeout(() => {
    w.onafterprint = () => w.close();   // ปิดหน้าต่างหลังพิมพ์ / ยกเลิก
    w.print();
  }, 500);
}

/* ─────────── downloadReceiptPdf ───────────
   สร้างไฟล์ PDF จาก element ที่มีหน้าใบเสร็จ .receipt-page (โหลด lib แบบ lazy) */
export async function downloadReceiptPdf(containerEl, filename = "receipt.pdf") {
  if (!containerEl) return;
  const pages = containerEl.querySelectorAll(".receipt-page");
  if (!pages.length) return;

  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);

  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  // ขอบเท่ากับตอนพิมพ์: @page margin 15mm 18mm
  const MARGIN_X = 18, MARGIN_Y = 15;
  const pageW = 210 - MARGIN_X * 2;
  const pageH = 297 - MARGIN_Y * 2;

  for (let i = 0; i < pages.length; i++) {
    const canvas = await html2canvas(pages[i], {
      scale: 2, useCORS: true, backgroundColor: "#ffffff",
      // ตัด padding ของตัวเอกสารออกเหมือนตอนพิมพ์ (@page margin ทำหน้าที่เป็นขอบแทน)
      onclone: (_doc, el) => { el.style.padding = "0"; el.style.minHeight = "auto"; },
    });
    const ratio = canvas.height / canvas.width;
    let w = pageW, h = pageW * ratio;
    if (h > pageH) { h = pageH; w = pageH / ratio; }
    if (i > 0) pdf.addPage();
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", (210 - w) / 2, MARGIN_Y, w, h);
  }
  pdf.save(filename);
}
